/* eslint-disable @typescript-eslint/no-explicit-any */
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { Link } from '@wsphere/links/domain';
import { LinksWebService } from '@wsphere/links/web';
import { Claim } from '@wsphere/warranties/domain';
import { catchError, combineLatest, map, of, ReplaySubject, switchMap, tap } from 'rxjs';
import { ClaimService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-timeline',
  templateUrl: './claim-timeline.component.html',
  standalone: false,
})
export class ClaimTimelineComponent {
  constructor(
    private linkSvc: LinksWebService,
    private breakpointObserver: BreakpointObserver,
    private claimSvc: ClaimService
  ) {}

  @PropertyListener('claim') claim$ = new ReplaySubject<Claim.Model>(1);
  @Input() claim: Claim.Model | null = null;

  @Input() isCustomerPortal = false;

  /** Off on the workspace, where there's nothing to fold. */
  @Input() collapsible = true;

  open = true;

  linkType: Link.ObjectType | null = null;
  linkTypes = [
    Link.ObjectType.CLAIM,
    Link.ObjectType.CLAIM_ITEM,
    Link.ObjectType.NOTE,
    Link.ObjectType.DOCUMENT,
    Link.ObjectType.PAYMENT,
  ];

  isMobile$ = this.breakpointObserver
    .observe([Breakpoints.XSmall, Breakpoints.Small])
    .pipe(map(state => state.matches));

  refresh$ = this.linkSvc.refreshActivity$;

  protected loading = false;

  activities$ = combineLatest([this.claim$, this.refresh$]).pipe(
    tap(() => (this.loading = true)),
    switchMap(async ([claim]) => {
      const activities = await this.linkSvc.list(
        { id: claim.id, objectType: Link.ObjectType.CLAIM },
        {
          limit: 100,
          owner: { id: claim.id, objectType: Link.ObjectType.CLAIM },
          linkType: Link.ObjectType.ACTIVITY,
        }
      );

      if (this.isCustomerPortal) {
        return {
          ...activities,
          items: activities.items.filter(activity => activity.activity_1?.feed?.includes('customer')),
        };
      } else return activities;
    }),
    map(data => data.items),
    map(links => links.map(link => link.activity_1).filter(activity => activity)), // Filter out null/undefined activities
    map(activities => activities.sort((a, b) => (b?.created?.getTime() || 0) - (a?.created?.getTime() || 0))), // Sort by created date
    catchError(error => {
      console.error('Error loading claim activities:', error);
      return of([]);
    }),
    // Clear loading in the same emission as activities so async-pipe CD picks it up.
    // A delayed setTimeout left loading=true until an unrelated event (e.g. hover) ran CD.
    tap(() => (this.loading = false))
  );

  setLinkType(type: Link.ObjectType) {
    this.linkType = type;
  }

  refresh() {
    this.refresh$.next(null);
  }
}
